/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Dispatch, SetStateAction } from 'react';

import LinearProgress from '@mui/material/LinearProgress';
import {
  DataGrid,
  DataGridProps,
  gridClasses,
  GridColDef,
  GridColumnMenu,
  GridColumnMenuProps,
  GridPaginationModel,
  GridRowId,
  GridSlots,
  GridSortModel,
} from '@mui/x-data-grid';

import CustomPagination from './Pagination';
import { IObject } from '../../interfaces/IObject';

interface Props extends DataGridProps {
  rows: IObject[];
  columns: GridColDef[];
  rowCount?: number;
  isLoading?: boolean;
  setSelectedRow?: Dispatch<SetStateAction<GridRowId[]>>;
  pageNumber: number;
  setPageNumberChange: Dispatch<SetStateAction<number>>;
  setSorting?: Dispatch<SetStateAction<GridSortModel>>;
  checkboxSelection?: boolean;
  maxSelected?: number;
  selectedRow?: GridRowId[];
  disableMultipleRowSelection?: boolean;
}

function CustomColumnMenu(props: GridColumnMenuProps) {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        // Hide `columnMenuColumnsItem`
        columnMenuColumnsItem: null,
      }}
    />
  );
}

const DataGridTable = (props: Props) => {
  const {
    rows,
    columns,
    rowCount,
    isLoading,
    setSelectedRow,
    setPageNumberChange,
    setSorting,
    maxSelected,
    selectedRow,
    checkboxSelection = true,
    ...rest
  } = props;

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  const handlePaginationModalCHange = React.useCallback(
    (value: GridPaginationModel) => {
      setPaginationModel(value);
      setPageNumberChange(value.page);
    },
    [setPageNumberChange]
  );
  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      setSorting?.([...sortModel]);
    },
    [setSorting]
  );

  return (
    <div
      style={{
        height: '80vh',
        width: '100%',
      }}
    >
      <DataGrid
        pagination
        rows={rows}
        columns={columns}
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick
        pageSizeOptions={[10]}
        rowCount={rowCount || 0}
        loading={isLoading}
        paginationModel={paginationModel}
        paginationMode="server"
        filterMode="server"
        sortingMode="server"
        onSortModelChange={handleSortModelChange}
        onPaginationModelChange={handlePaginationModalCHange}
        disableColumnMenu
        slots={{
          loadingOverlay: LinearProgress as GridSlots['loadingOverlay'],
          columnMenu: CustomColumnMenu,
          pagination: CustomPagination,
        }}
        rowSelectionModel={selectedRow}
        onRowSelectionModelChange={(newRowSelectionModel: any) => {
          if (maxSelected && newRowSelectionModel.length > maxSelected) {
            const selectionSet = new Set(selectedRow);
            const result = newRowSelectionModel.filter((s: any) => !selectionSet.has(s));

            setSelectedRow?.(result);
          } else {
            setSelectedRow?.(newRowSelectionModel);
          }
        }}
        sx={{
          '.MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '&.MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-columnHeaders [role=row]': {
            backgroundColor: 'riPrimary.10',
            fontSize: 16,
            borderRadius: '50px',
            borderBottom: 'none',
          },
          [`.${gridClasses.cell}`]: {
            display: 'flex',
            alignItems: 'center',
          },
        }}
        {...rest}
      />
    </div>
  );
};

export default DataGridTable;
